/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Build readable sentences for alerts triggers.
 *
 * @param description - the alert’s description (e.g. “Detects when interface state is down”)
 * @param fieldLabel  - a short name for the field to display (e.g. “Bandwidth” or “Interface state”)
 * @param operator    - the raw operator (e.g. "greater_than_or_equal" or "!=")
 * @param value       - the raw threshold (number|string)
 * @param meta        - the template’s thresholdMetadata (guidance and unit)
 * @param interval    - the checking interval ("5m", "10s", etc)
 */

export interface ThresholdMetadata {
  type: "percentage" | "bandwidth" | "latency" | "count" | "string";
  unit: string;
  range?: { min?: number; max?: number };
  examples?: Array<{ value: number | string; description: string }>;
  description: string;
  customizable: boolean;
}

// map API operators to symbols
export const OPERATOR_SYMBOLS: Record<string, string> = {
  greater_than: ">",
  greater_than_or_equal: "≥",
  less_than: "<",
  less_than_or_equal: "≤",
  equal: "=",
  "!=": "≠",
};

/**
 * Given a raw threshold value and its metadata, returns a nicely formatted string.
 */
export function formatThresholdValue(
  value: number | string,
  meta?: ThresholdMetadata
): string {
  if (!meta) return String(value);

  switch (meta.type) {
    case "percentage":
    case "latency":
    case "count": {
      // e.g. "90%"
      return `${value}${meta.unit}`;
    }

    case "bandwidth": {
      // API already gives e.g. "500 Mbps"
      return String(value);
    }

    case "string": {
      // look up an example for a human-readable label
      const ex = meta.examples?.find((e) => e.value === value);
      return ex?.description ?? String(value);
    }

    default: {
      return String(value);
    }
  }
}

/**
 * Turns "5m" into "5 minutes", "30s" into "30 seconds", etc.
 */
export function formatInterval(interval: string): string {
  const m = interval.match(/^(\d+)([smhd])$/);
  if (!m) return interval;

  const [, n, unit] = m;
  const names: Record<string, string> = {
    s: "second",
    m: "minute",
    h: "hour",
    d: "day",
  };
  const word = names[unit] ?? unit;
  return `${n} ${word}${n === "1" ? "" : "s"}`;
}

export function buildConditionSentence(
  description: string,
  fieldLabel: string,
  operator: string,
  value: number | string,
  interval: string,
  meta: ThresholdMetadata | undefined
): string {
  const desc = description ?? "";
  const formattedInterval = formatInterval(interval);

  //If there is no valid threshold/value, just use the description:
  if (operator === "" || value === "" || value == null) {
    // strip the “Detects when” prefix
    let whenClause = desc.replace(/^Detects when\s*/i, "");
    // lowercase its first letter
    whenClause = whenClause.charAt(0).toLowerCase() + whenClause.slice(1);
    return `When ${whenClause} for ${formattedInterval}`;
  }

  const rawOp = operator;
  const opSym = OPERATOR_SYMBOLS[rawOp] ?? rawOp.replace(/_/g, " ");
  const formattedValue = formatThresholdValue(value, meta);

  if (meta?.type === "string" && !meta.customizable) {
    // strip the "Detects when " prefix
    let whenClause = desc.replace(/^Detects when\s*/i, "");
    // optionally drop a trailing " state"
    whenClause = whenClause.replace(/\b state\b/i, "");
    // now: "BGP session is not established"
    return `When ${whenClause} for ${formattedInterval}`;
  }

  // default case → name the field explicitly
  // e.g. "When bandwidth monitoring ≥ 90% for 5 minutes"
  return `When ${fieldLabel} ${opSym} ${formattedValue} for ${formattedInterval}`;
}

export function getTemplateLabel(id: string, humanName: string): string {
  // drop the trailing “-template”
  const base = id.replace(/-template$/, "");
  const parts = base.split("-");
  const qualifier = parts[parts.length - 1];               // e.g. "high"
  const coreWords = parts.slice(0, -1).map(p => 
    p.charAt(0).toUpperCase() + p.slice(1)
  );                                                       // ["Bandwidth","Utilization"]
  const coreName = coreWords.join(" ");

  //if the original humanName already ends with qualifier, just use humanName
  if ( new RegExp(`${qualifier}$`, "i").test(humanName) ) {
    return humanName;
  }
  // otherwise prepend qualifier:
  return `${qualifier.charAt(0).toUpperCase() + qualifier.slice(1)} ${humanName}`;
}
